import {Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Individu, IndividuService,} from '@Common/core/services';
import {RoleEntite} from '@Services/gestion-universite/roles/role-entite';
import {ManageRoleService} from '@Services/gestion-universite/roles/manage-roles.service';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {LdapUser} from '@Services/ldap/ldap/ldap-user.model';
import {LdapService} from '@Services/ldap/ldap/ldap.service';
import {SubSink} from '@Common/core/utils/subsink';
import {concatMap, finalize} from 'rxjs/operators';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';

/** tests purpose bug https://v16.material.angular.io/components/chips/examples **/
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {MatChipEditedEvent, MatChipInputEvent} from "@angular/material/chips";
export interface Fruit {
  name: string;
}

/**
 * Gestion des rôles individus
 * - recherche LDAP
 * - get ou création individu lors de la sélection d'un résultat de la recherche LDAP
 * - suppression de tous les accès rôles
 */
@Component({
  selector: 'p8-role-entites',
  templateUrl: './role-entites.component.html',
  styleUrls: ['./role-entites.component.scss'],
})
export class RoleEntitesComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  role: string;

  @Input()
  entite: string;

  @Input()
  role_label: string;

  @Input()
  role_description: string;

  @Input()
  expanded = false;

  @Input()
  message_confirmation = 'Confirmez-vous la suppression des droits pour cette personne ?';

  @Input()
  canDeleteAccessBulk = false;

  @Input()
  buttonDeleteAllAccess = 'Enlever tous les accès';
  @Input()
  messageConfirmationDelete = 'Confirmez-vous la suppression de tous les accès ?';

  /**
   * L'URL des gestion de rôles, à défaut de /gestion_universite/manageroles/ le cas échéant
   */
  @Input() urlApiRole = null;

  /**
   * L'URL de gestion individu get_or_create, à défaut de /gestion_universite/individu le cas échéant
   */
  @Input() urlApiIndividu = null;

  /**
   * Options de recherche
   */
  @Input() scope = false;
  @Input() defaultScopes = '';
  @Input() scopesToDisplay = ['employee', 'faculty', 'researcher', 'student'];

  alreadyOpened = false;

  roleEntites: RoleEntite[];

  private individuConnected: Individu = null;

  loadingRole = false;
  subSink = new SubSink();

  constructor(private manageRoleService: ManageRoleService,
              private snackSvc: SnackBarToastyService,
              private individuService: IndividuService,
              private ldapService: LdapService,
              private dialog: MatDialog) {

  }

  ngOnInit() {
    this.subSink.sink = this.individuService.individu$
      .subscribe((ind) => this.individuConnected = ind);
  }

  ngOnChanges(changes: SimpleChanges) {
  }

  load() {
    if (! this.alreadyOpened) {
      this.loadingRole = true;
      const urlQS = new Map<string, any>();
      urlQS.set('role', this.role).set('entite', this.entite);

      this.subSink.sink = this.manageRoleService
        .listAllItems(urlQS)
        .pipe(finalize(() => this.loadingRole = false))
        .subscribe(roles => this.roleEntites = roles.list);
    }

    this.alreadyOpened = true;
  }

  isIndConnected(individu) {
    if (this.individuConnected) {
      return individu.uid === this.individuConnected.uid;
    }

    return false;
  }

  /**
   * A la sélection de l'individu LDAP
   * => obtention ou création de l'individu à partir de son uid LDAP trouvé
   * => création du rôle pour cet individu
   * @param ldapUser
   */
  onSelectIndiv(ldapUser: LdapUser) {
    const uid = ldapUser.uid[0];

    if (this.roleEntites.find(i => i.individu.uid === uid)) {
      return;
    }
    this.loadingRole = true;
    this.subSink.sink = this.individuService
      .getOrCreate(uid, this.urlApiIndividu)
      .pipe(
        concatMap(_individu => this.manageRoleService.createRoleBase(this.role, uid, this.entite, this.urlApiRole)),
        finalize(() => this.loadingRole = false))
      .subscribe(_ => {
          this.alreadyOpened = false;
          this.load();
      });
  }
  confirmDeleteRole(id) {
    const data = new DialogData();
    data.title = 'Suppression droits';
    data.message = this.message_confirmation;

    const modalConfirm = this.dialog.open(DialogValidationComponent, { data: data, disableClose: true});
    modalConfirm.updatePosition({top: '40px'});
    this.subSink.sink = modalConfirm.afterClosed().subscribe(confirm => {
      if (confirm === true) {
        this.loadingRole = true;
        this.subSink.sink = this.manageRoleService
          .deleteRole(id, this.urlApiRole)
          .pipe(finalize(() => this.loadingRole = false))
          .subscribe(() => {
            this.alreadyOpened = false;
            this.load();
        });
      }
    });
  }
  deleteAllAccess() {
    this.loadingRole = true;
    const rolesToDelete = this.roleEntites.map(_entite => _entite.id);
    this.subSink.sink = this.manageRoleService
      .deleteRoleBulk(rolesToDelete, this.urlApiRole)
      .pipe(finalize(() => this.loadingRole = false))
      .subscribe(() => {
        this.snackSvc.toasty('Suppression d\'accès', 'Tous les accès ont été supprimés', typeToast.success);
        this.alreadyOpened = false;
        this.load();
    });
  }
  confirmDeleteAllAccess() {
    const data = new DialogData();
    data.title = `Suppression d'accès`;
    data.iconType = 'warning';
    data.message = this.messageConfirmationDelete;
    const dialogRef = this.dialog.open(DialogValidationComponent,  { data: data, disableClose: true });
    dialogRef.updatePosition({top: '100px'});
    dialogRef.afterClosed().subscribe(_confirm => {
      if (_confirm) {
        this.deleteAllAccess();
      }
    });
  }
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

}
